import { useCallback, useContext } from 'react';
import { Dayjs } from 'dayjs';
import { FITNESS_VILLAGE_COPY_EN, FitnessVillageCopy } from '../Utils/fitnessVillageCopy';
import { FITNESS_VILLAGE_COPY_AR } from '../Utils/fitnessVillageCopy.ar';
import { ASA_COPY_EN, isAsaCompany } from '../Utils/asaBrand';
import { CLUB15_COPY_EN, isClub15Company } from '../Utils/club15Brand';
import { CheckoutContext, CheckoutContextProps } from '../context/CheckoutContext';
import { DfcLocale, formatDfcDate } from '../Utils/dfcLocale';
import { translateDfcMessage } from '../Utils/dfcMessages';
import { useDfcPageDirection } from '../theme/dfcDirection';

const FITNESS_VILLAGE_COPY: Record<DfcLocale, FitnessVillageCopy> = {
  en: FITNESS_VILLAGE_COPY_EN,
  ar: FITNESS_VILLAGE_COPY_AR,
};

/**
 * The one way a DFC village component obtains its copy: English, or Arabic when the visitor
 * picked it in the header. Any other company always gets English.
 */
const useFitnessVillageCopy = (): FitnessVillageCopy => {
  const { company } = useContext(CheckoutContext) as CheckoutContextProps;
  const { locale } = useDfcPageDirection();
  // All Sports Arena renders the village design with its own English (`asaBrand.ts`).
  // Club 15 the same, with its own (`club15Brand.ts`).
  if (isClub15Company(company)) return CLUB15_COPY_EN;
  return isAsaCompany(company) ? ASA_COPY_EN : FITNESS_VILLAGE_COPY[locale];
};

/** Formats a date in the village's language: `formatDate(day, 'ddd D MMM')` → "Tue 3 Nov" / "ثلاثاء 3 نوفمبر". */
export const useDfcFormatDate = (): ((date: Dayjs, format: string) => string) => {
  const { locale } = useDfcPageDirection();
  return useCallback(
    (date: Dayjs, format: string) => formatDfcDate(date, format, locale),
    [locale]
  );
};

/**
 * Translates an English message from shared code (a validation error, a page title) as it
 * renders: `localize(errors.email?.message)`. See `localizeMessage` for toasts.
 */
export const useLocalizeMessage = (): ((english: string) => string) => {
  const { locale } = useDfcPageDirection();
  return useCallback((english: string) => translateDfcMessage(english, locale), [locale]);
};

export default useFitnessVillageCopy;
